import {useState} from "react";
import {Card} from "../../cdx-models/Card";
import {Root} from "../../cdx-models/Root";
import {useModalWithData} from "../../components/Modals";
import {Col, css, DSButton, DSIconPyramid, Row, Text} from "@cdx/ds";
import {getChildCards, getWorkflowItemsWithZones} from "./workflow-utils";
import {WorkflowItem, WorkflowItemId} from "../../cdx-models/WorkflowItem";
import WorkflowItemComp from "./WorkflowItem";
import {api} from "../../lib/api";
import {checkIfPresent} from "../../lib/mate/mate-utils";
import {cx, RevealSpinner} from "@cdx/common";
import useSet from "../../lib/hooks/useSet";
import SelectionIconButton from "../../components/Card/SelectionIcon";
import useMutation from "../../lib/hooks/useMutation";
import {waitForResultPromise} from "../../lib/wait-for-result";
import {pluralize} from "../../lib/utils";
import messenger from "../../lib/messenger";
import {ReactComponent as CornerTlSvg} from "../hand-queue/imgs/corner-brass-tl.svg";
import {ReactComponent as CornerTrSvg} from "../hand-queue/imgs/corner-brass-tr.svg";
import {ReactComponent as CornerBlSvg} from "../hand-queue/imgs/corner-brass-bl.svg";
import {ReactComponent as CornerBrSvg} from "../hand-queue/imgs/corner-brass-br.svg";
import {handQueueStyles} from "../hand-queue/hand-queue.css";
import JourneyZoneBg from "./JourneyZoneBg";

const Zone = (props: {
  items: WorkflowItem[];
  label: string | null;
  root: Root;
  selection: ItemSelection;
  prevAppliedItemIds: Set<WorkflowItemId>;
  zoneIdx: number;
}) => {
  const {items, label, root, selection, prevAppliedItemIds, zoneIdx} = props;
  const itemIds = items.map((item) => item.itemId as WorkflowItemId);
  const areAllSelected = itemIds.every((id) => selection.isSelected(id));
  const prefix = areAllSelected ? "Deselect" : "Select";
  const tooltip = items.length === 1 ? `${prefix} one step` : `${prefix} all ${items.length} steps`;

  return (
    <Col sp="12px">
      <Row align="center" sp="8px">
        <SelectionIconButton
          tooltip={tooltip}
          onClick={() =>
            areAllSelected ? selection.deselectMany(itemIds) : selection.selectMany(itemIds)
          }
          active={areAllSelected}
          onLight={false}
        />
        <Row sp="8px">
          <DSIconPyramid size={16} className={css({color: "secondary"})} />
          <Text type="label14" color="secondary" as="h2">
            {label || "Default Steps"}
          </Text>
        </Row>
      </Row>
      <Row
        wrap
        sp="16px"
        pa="16px"
        rounded={4}
        colorTheme="journeyBlue"
        borderColor="default"
        borderWidth={1}
        bg="foreground"
        relative
        overflow="hidden"
      >
        <JourneyZoneBg initialOffset={zoneIdx * 400} />
        {items.map((item) => (
          <Col sp="8px" relative>
            <WorkflowItemComp
              key={item.itemId}
              root={root}
              item={item}
              isSelecting
              onSelect={selection.select}
              onDeselect={selection.deselect}
              isSelected={selection.isSelected(item.itemId as WorkflowItemId)}
              canModify={false}
            />
            {prevAppliedItemIds.has(item.itemId as WorkflowItemId) && (
              <Col align="center" height="16px">
                <Text type="label11" color="secondary">
                  Already Created
                </Text>
              </Col>
            )}
          </Col>
        ))}
      </Row>
    </Col>
  );
};

type ItemSelection = {
  isSelecting: boolean;
  isSelected(itemId: WorkflowItemId): boolean;
  select(itemId: WorkflowItemId): void;
  deselect(itemId: WorkflowItemId): void;
  selectMany(itemIds: WorkflowItemId[]): void;
  deselectMany(itemIds: WorkflowItemId[]): void;
  getSelected(): WorkflowItemId[];
  getSize(): number;
};

const useItemSelection = (initialFn: () => WorkflowItemId[]): ItemSelection => {
  const [set, actions] = useSet(() => new Set(initialFn()));
  return {
    isSelecting: set.size > 0,
    isSelected: (itemId: WorkflowItemId) => set.has(itemId),
    select: (itemId: WorkflowItemId) => actions.add(itemId),
    deselect: (itemId: WorkflowItemId) => actions.remove(itemId),
    selectMany: (itemIds: WorkflowItemId[]) => actions.set(new Set([...set, ...itemIds])),
    deselectMany: (itemIds: WorkflowItemId[]) => {
      const existing = new Set(set);
      itemIds.forEach((id) => existing.delete(id));
      actions.set(existing);
    },
    getSelected: () => [...set],
    getSize: () => set.size,
  };
};

type WithLoadedZonesProps = {
  zoneMap: Map<string | null, WorkflowItem[]>;
  items: WorkflowItem[];
  prevAppliedItemIds: Set<WorkflowItemId>;
} & WorkflowApplicationOverlayProps;
const WithLoadedZones = (props: WithLoadedZonesProps) => {
  const {zoneMap, root, items, card, onClose, prevAppliedItemIds} = props;
  const selection = useItemSelection(() =>
    items.map((i) => i.itemId as WorkflowItemId).filter((id) => !prevAppliedItemIds.has(id))
  );

  const [applyWorkflow] = useMutation("workflows", "apply");
  const handleClick = () => {
    return waitForResultPromise(() => {
      const selectedItems = selection
        .getSelected()
        .map((id) => api.getModel({modelName: "workflowItem", id}));
      return selectedItems.some((w: any) => w.targetDeck && w.targetDeck.$meta.isDeleted());
    }).then((isInaccessible) => {
      if (isInaccessible) {
        return Promise.reject(
          "Journey can't be created as you don't have access to all target decks."
        );
      } else {
        return applyWorkflow({cardId: card.cardId, itemIds: selection.getSelected()}).then(() => {
          onClose();
          messenger.send(`${pluralize(selection.getSize(), "Journey Card")} created`);
        });
      }
    });
  };

  return (
    <Col sp="32px">
      <Col sp="8px">
        <Text type="title24" color="primary">
          Start Journey
        </Text>
        <Text type="prose14" color="secondary">
          Select the cards that you want to create.
        </Text>
      </Col>
      {[...zoneMap]
        .filter(([, zItems]) => zItems.length > 0)
        .map(([label, zoneItems], idx) => (
          <Zone
            items={zoneItems}
            label={label}
            key={label}
            root={root}
            selection={selection}
            prevAppliedItemIds={prevAppliedItemIds}
            zoneIdx={idx}
          />
        ))}
      <Row justify="center" sp="8px">
        <DSButton size="lg" variant="secondary" onClick={onClose}>
          Cancel
        </DSButton>
        <DSButton size="lg" disabled={!selection.isSelecting} onClick={handleClick}>
          Create {pluralize(selection.getSize(), "Card")}
        </DSButton>
      </Row>
    </Col>
  );
};

const Corner = {
  Tl: CornerTlSvg,
  Tr: CornerTrSvg,
  Bl: CornerBlSvg,
  Br: CornerBrSvg,
};

const Bg = ({children, ...props}: any) => {
  return (
    <Col relative colorTheme="journeyDimBlue" elevation={200} bg="foreground">
      <Corner.Tl className={cx(handQueueStyles.corner.base, handQueueStyles.corner.topLeft)} />
      <Corner.Tr className={cx(handQueueStyles.corner.base, handQueueStyles.corner.topRight)} />
      <Corner.Bl className={cx(handQueueStyles.corner.base, handQueueStyles.corner.bottomLeft)} />
      <Corner.Br className={cx(handQueueStyles.corner.base, handQueueStyles.corner.bottomRight)} />
      <Col {...props}>{children}</Col>
    </Col>
  );
};

type WorkflowApplicationOverlayProps = {
  root: Root;
  card: Card;
  onClose: () => void;
};
const WorkflowApplicationOverlay = (props: WorkflowApplicationOverlayProps) => {
  const {card} = props;
  const deck = card.deck;
  if (!deck) return <div>Card is not in a deck</div>;
  const {
    isLoaded,
    result: {zoneMap, items, prevAppliedItemIds},
  } = checkIfPresent(() => {
    const childCards = getChildCards(card);
    const prevApplied = childCards
      .map((c) => c.sourceWorkflowItem?.itemId as WorkflowItemId)
      .filter(Boolean);
    return {
      prevAppliedItemIds: new Set(prevApplied),
      ...getWorkflowItemsWithZones(deck),
    };
  }, api);

  return (
    <Bg pa={5}>
      <RevealSpinner show={!isLoaded} />
      {isLoaded && (
        <WithLoadedZones
          zoneMap={zoneMap}
          items={items}
          prevAppliedItemIds={prevAppliedItemIds}
          {...props}
        />
      )}
    </Bg>
  );
};

export const useWorkflowApplicationOverlay = (
  props: Omit<WorkflowApplicationOverlayProps, "onClose">
) => {
  const [modalOpen, setModalOpen] = useState(false);
  const renderModal = useModalWithData(modalOpen, {
    onClose: () => setModalOpen(false),
    width: 984, // fine tuned to fit exactly 7 cards in a row
    hideClose: true,
    blueBackdrop: true,
  });
  return {
    modal: renderModal(() => (
      <WorkflowApplicationOverlay {...props} onClose={() => setModalOpen(false)} />
    )),
    setModalOpen,
  };
};
